import { useEffect, useMemo, useRef, useState } from 'react'
import { useUserSearch } from '../api/queries'
import type { UserSuggestion } from '../api/types'
import { ALLOWED_DOMAIN, isAllowedEmail } from '../lib/config'
import { cn } from '../lib/cn'
import { Button, Spinner } from './ui'

/**
 * Выбор участников.
 *
 * В компании на 350 человек однофамильцы неизбежны, поэтому в подсказке всегда видно
 * должность, а при совпадении имён сервер поднимает флаг show_email и рядом появляется
 * адрес. Позвать не того — вопрос времени, если различать людей нечем.
 *
 * Кого нет в системе, можно позвать по адресу в домене компании: учётная запись
 * заводится в статусе ожидания и активируется при первом входе. Без этого пригласить
 * можно было бы только тех, кто уже сам зарегистрировался.
 */

export interface PickedParticipant {
  user_id?: number
  email: string
  display_name: string
  position?: string
  /** true — человека в системе ещё нет, зовём по адресу. */
  pending?: boolean
}

export function ParticipantPicker({
  value,
  onChange,
  disabled,
}: {
  value: PickedParticipant[]
  onChange: (next: PickedParticipant[]) => void
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  // Печатать быстрее, чем ходит запрос, — обычное дело; без паузы поиск дёргается.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const { data: found, isFetching } = useUserSearch(debounced)

  const alreadyPicked = useMemo(
    () => new Set(value.map((participant) => participant.email.toLowerCase())),
    [value],
  )

  const suggestions = (found ?? []).filter(
    (suggestion) => !alreadyPicked.has(suggestion.email.toLowerCase()),
  )

  const typedEmail = query.trim().toLowerCase()
  const canInviteByEmail =
    isAllowedEmail(typedEmail) &&
    !alreadyPicked.has(typedEmail) &&
    !suggestions.some((suggestion) => suggestion.email.toLowerCase() === typedEmail)

  function add(participant: PickedParticipant) {
    onChange([...value, participant])
    setQuery('')
    setDebounced('')
    setOpen(false)
  }

  function addFromSuggestion(suggestion: UserSuggestion) {
    add({
      user_id: suggestion.id,
      email: suggestion.email,
      display_name: suggestion.display_name,
      position: suggestion.position,
    })
  }

  function remove(email: string) {
    onChange(value.filter((participant) => participant.email !== email))
  }

  return (
    <div ref={boxRef} className="relative">
      {value.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {value.map((participant) => (
            <li
              key={participant.email}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm',
                participant.pending
                  ? 'border-dashed border-accent/50 bg-accent-soft/40'
                  : 'border-line bg-surface-2',
              )}
              title={participant.pending ? 'Приглашение уйдёт на почту, учётная запись заведётся при первом входе' : participant.email}
            >
              <span className="font-medium">{participant.display_name}</span>
              {participant.position && <span className="text-muted">{participant.position}</span>}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(participant.email)}
                  aria-label={`Убрать ${participant.display_name}`}
                  className="text-muted transition hover:text-state-busy"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="relative">
        <input
          className="field"
          value={query}
          disabled={disabled}
          /* Подпись «Участники» стоит над блоком заголовком, а не label-ом,
             поэтому имя поля задаётся здесь: подсказка placeholder-ом исчезает
             при первом же символе и скринридеру не достаётся. */
          aria-label="Поиск участника по имени, фамилии или адресу"
          role="combobox"
          aria-expanded={open && query.trim().length >= 2}
          aria-autocomplete="list"
          placeholder={`Имя, фамилия или адрес в домене ${ALLOWED_DOMAIN}`}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            // Escape закрывает подсказку, а не улетает в диалог и не закрывает его:
            // человек ждёт, что первое нажатие уберёт список.
            if (event.key === 'Escape' && open) {
              event.stopPropagation()
              setOpen(false)
            }
          }}
        />
        {isFetching && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted">
            <Spinner />
          </span>
        )}
      </div>

      {/*
        Список подсказок стоит в потоке, а не поверх страницы.

        Раньше он был absolute и накрывал соседнюю карточку: нижний край панели
        приходился ровно на середину строки «Повторять каждую неделю», и надпись
        читалась разрезанной пополам — вместе с чекбоксом, до которого в этот момент
        было не дотянуться. Всплывающий список хорош там, где под ним пусто; в форме
        под ним всегда что-то есть.

        Плата — сдвиг того, что ниже, на время поиска. Она честнее: ничего не закрыто,
        ничего не разрезано, и видно, что список — часть поля участников.
      */}
      {open && query.trim().length >= 2 && (
        <div className="relative z-20 mt-1 max-h-72 w-full overflow-auto rounded-card border border-line bg-surface shadow-pop">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => addFromSuggestion(suggestion)}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition hover:bg-surface-2"
            >
              <span className="text-sm font-medium">{suggestion.display_name}</span>
              <span className="text-xs text-muted">
                {suggestion.position || 'Должность не указана'}
                {/* Адрес показываем, только когда в выдаче есть тёзка: иначе он лишний шум. */}
                {suggestion.show_email && <span className="tnum"> · {suggestion.email}</span>}
              </span>
            </button>
          ))}

          {suggestions.length === 0 && !isFetching && !canInviteByEmail && (
            <p className="px-3 py-3 text-sm text-muted">
              Никого не нашли. Если человек ещё ни разу не заходил в систему, введите его рабочий
              адрес целиком — приглашение уйдёт на почту.
            </p>
          )}

          {canInviteByEmail && (
            <div className="border-t border-line p-3">
              <p className="mb-2 text-sm text-muted">
                Этого адреса в системе нет. Можно позвать по почте: учётная запись заведётся
                при первом входе.
              </p>
              <Button
                type="button"
                size="sm"
                variant="quiet"
                onClick={() =>
                  add({ email: typedEmail, display_name: typedEmail, pending: true })
                }
              >
                Позвать {typedEmail}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
